/**
 * NutrientVisualizationPage.tsx
 * 
 * Main page for nutrient profile visualization and comparison
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Divider,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  TextField,
  useTheme,
  IconButton,
} from '@mui/material';
import Grid from '../components/common/Grid';
import NutrientProfileChart from '../components/nutrient-visualization/NutrientProfileChart';
import NutrientComparisonGraph from '../components/nutrient-visualization/NutrientComparisonGraph';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
// Comment out this import until the module is available
// import { selectFormulations } from '../slices/formulationSlice';
import { NutrientProfile } from '../types/nutrient';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import ColorLensIcon from '@mui/icons-material/ColorLens';
// Comment out this import until the module is available
// import { saveAs } from 'file-saver';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nutrient-viz-tabpanel-${index}`}
      aria-labelledby={`nutrient-viz-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `nutrient-viz-tab-${index}`,
    'aria-controls': `nutrient-viz-tabpanel-${index}`,
  };
}

// Sample profiles for testing (replace with actual data from store)
const SAMPLE_PROFILES = [
  {
    id: 'profile1',
    name: 'Lettuce Nutrient Solution',
    profile: {
      N: 150,
      P: 50,
      K: 200,
      Ca: 180,
      Mg: 50,
      S: 60,
      Fe: 3,
      Mn: 0.8,
      Zn: 0.3,
      B: 0.4,
      Cu: 0.1,
      Mo: 0.06
    }
  },
  {
    id: 'profile2',
    name: 'Tomato Nutrient Solution',
    profile: {
      N: 180,
      P: 60,
      K: 300,
      Ca: 200,
      Mg: 60,
      S: 80,
      Fe: 4,
      Mn: 1.0,
      Zn: 0.4,
      B: 0.7,
      Cu: 0.1,
      Mo: 0.06
    }
  },
  {
    id: 'profile3',
    name: 'Strawberry Nutrient Solution',
    profile: {
      N: 70,
      P: 50,
      K: 150,
      Ca: 190,
      Mg: 50,
      S: 50,
      Fe: 3.5,
      Mn: 1.0,
      Zn: 0.3,
      B: 0.6,
      Cu: 0.1,
      Mo: 0.05
    }
  }
];

// Predefined colors for profiles
const PROFILE_COLORS = [
  '#5470c6', '#91cc75', '#fac858', '#ee6666',
  '#73c0de', '#3ba272', '#fc8452', '#9a60b4'
];

const NutrientVisualizationPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  // Temporarily comment out until formulations slice is available
  // const formulations = useAppSelector(selectFormulations);
  const formulations: any[] = [];
  
  const [tabValue, setTabValue] = useState(0);
  const [targetProfile, setTargetProfile] = useState<NutrientProfile | null>(null);
  const [actualProfile, setActualProfile] = useState<NutrientProfile | null>(null);
  const [selectedProfiles, setSelectedProfiles] = useState<any[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleSaveChart = (dataUrl: string) => {
    try {
      // Convert data URL to Blob
      const byteString = atob(dataUrl.split(',')[1]);
      const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      
      const blob = new Blob([ab], { type: mimeString });
      
      // Save the file - using direct download method instead of file-saver
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `nutrient-chart-${new Date().toISOString().slice(0, 10)}.png`;
      downloadLink.click();
      
      setSnackbarMessage('Chart saved successfully');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving chart:', error);
      setSnackbarMessage('Failed to save chart');
      setSnackbarOpen(true);
    }
  };
  
  const handleAddProfile = () => {
    setDialogOpen(true);
  };
  
  const handleRemoveProfile = (id: string) => {
    setSelectedProfiles(selectedProfiles.filter(profile => profile.id !== id));
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  const handleProfileSelect = (profile: any) => {
    // Check if profile is already selected
    if (selectedProfiles.some(p => p.id === profile.id)) {
      handleRemoveProfile(profile.id);
      return;
    }
    
    // Add color to profile
    const newProfile = {
      ...profile,
      color: PROFILE_COLORS[selectedProfiles.length % PROFILE_COLORS.length]
    };
    
    setSelectedProfiles([...selectedProfiles, newProfile]);
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  // Set initial target/actual profiles for demonstration
  useEffect(() => {
    if (SAMPLE_PROFILES.length >= 2) {
      // Use type assertion to match the expected format
      setTargetProfile(SAMPLE_PROFILES[0].profile as unknown as NutrientProfile);
      setActualProfile(SAMPLE_PROFILES[1].profile as unknown as NutrientProfile);
      
      // Add first two profiles to comparison
      setSelectedProfiles([
        { ...SAMPLE_PROFILES[0], color: PROFILE_COLORS[0] },
        { ...SAMPLE_PROFILES[1], color: PROFILE_COLORS[1] }
      ]);
    }
  }, []);
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Nutrient Visualization
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Visualize and compare nutrient profiles for different formulations, crops, and growth stages.
      </Typography>
      
      <Paper sx={{ mt: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="nutrient visualization tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Profile Comparison" {...a11yProps(0)} />
          <Tab label="Multi-Profile Analysis" {...a11yProps(1)} />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Target vs. Actual Nutrient Profile
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => {
                    // TODO: Implement save functionality with a modal
                  }}
                >
                  Save Configuration
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Compare your target nutrient levels with the actual achieved levels in your solution.
              </Typography>
            </Grid>
            
            <Grid xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Target Profile
                </Typography>
                <TextField
                  select
                  fullWidth
                  label="Select Target Profile"
                  value="profile1"
                  size="small"
                  // Add onChange handler to update target profile
                >
                  {SAMPLE_PROFILES.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </TextField>
              </Paper>
            </Grid>
            
            <Grid xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Actual Profile
                </Typography>
                <TextField
                  select
                  fullWidth
                  label="Select Actual Profile"
                  value="profile2"
                  size="small"
                  // Add onChange handler to update actual profile
                >
                  {SAMPLE_PROFILES.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </TextField>
              </Paper>
            </Grid>
            
            <Grid xs={12}>
              <NutrientProfileChart
                targetProfile={targetProfile || undefined}
                actualProfile={actualProfile || undefined}
                onSaveChart={handleSaveChart}
              />
            </Grid>
          </Grid>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Multi-Profile Comparison
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Compare multiple nutrient profiles side by side to analyze differences between formulations or growth stages.
            </Typography>
          </Box>
          
          <NutrientComparisonGraph
            profiles={selectedProfiles}
            onAddProfile={handleAddProfile}
            onRemoveProfile={handleRemoveProfile}
            onSaveChart={handleSaveChart}
          />
        </TabPanel>
      </Paper>
      
      {/* Profile Selection Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Profiles to Compare
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <List sx={{ width: '100%' }}>
            {SAMPLE_PROFILES.map((profile) => {
              const isSelected = selectedProfiles.some(p => p.id === profile.id);
              
              return (
                <ListItemButton
                  key={profile.id}
                  onClick={() => handleProfileSelect(profile)}
                  selected={isSelected}
                  sx={{
                    borderLeft: isSelected ? 
                      `4px solid ${selectedProfiles.find(p => p.id === profile.id)?.color || theme.palette.primary.main}` : 
                      'none'
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={profile.name}
                    secondary={`${Object.keys(profile.profile).length} nutrients defined`}
                  />
                  {isSelected && (
                    <ColorLensIcon 
                      sx={{ 
                        color: selectedProfiles.find(p => p.id === profile.id)?.color || theme.palette.primary.main
                      }} 
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>Done</Button>
        </DialogActions>
      </Dialog>
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NutrientVisualizationPage;
